// src/pages/student/exams/StudentExamsPage.js
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import {
  Card, Badge, Spinner, Row, Col, Alert, Button, Modal, OverlayTrigger, Tooltip,
  Form, InputGroup
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useBrand, hexToRgba } from "../_brand";
import {
  collection, doc, getDoc, onSnapshot, query, where, limit,
  setDoc, updateDoc, serverTimestamp, increment
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { db } from "../../../services/firebase";

dayjs.extend(durationPlugin);

/* ---------------- helpers ---------------- */
const TOAST_ID = "student-exams";
const getDescScore = (att = {}) => {
  const v = att.evaluatedScore ?? att.score ?? att.marksObtained ?? att.obtained;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const getDescTotal = (exam = {}, att = {}) => {
  const v =
    att.totalMarks ??
    exam.totalMarks ??
    exam.maxScore ??
    exam.fullMarks ??
    exam.total ??
    null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const norm = (s) => String(s || "").trim().toLowerCase();
const upper = (s) => String(s || "").trim().toUpperCase();
const fmtShort = (d) => (d ? dayjs(d).format("DD MMM • HH:mm") : "");

/** Robustly prettify snake_case like 'weekly_test' -> 'Weekly Test' */
const prettyTitle = (val = "") =>
  String(val)
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/**
 * Extract the *admin-defined exam mode* (category) attached to an exam, if present.
 * We support several possible field names to be resilient:
 */
const getCategoryMeta = (e) => {
  const labelRaw =
    e?.testTypeLabel ||
    e?.examModeLabel ||
    e?.templateModeLabel ||
    e?.categoryLabel ||
    e?.modeLabel ||
    null;

  let keyRaw =
    e?.testType ||
    e?.test_type ||
    e?.examModeKey ||
    e?.examCategoryKey ||
    e?.templateModeKey ||
    e?.templateMode ||
    e?.category ||
    null;

  if (!keyRaw && labelRaw) {
    keyRaw = String(labelRaw).trim().toLowerCase().replace(/\s+/g, "_");
  }

  const run = String(e?.examMode || "").toLowerCase();
  if ((keyRaw === "live" || keyRaw === "mock") && !labelRaw) keyRaw = null;

  const value = keyRaw ? String(keyRaw).toLowerCase() : null;
  const label = labelRaw || (value ? prettyTitle(value) : null);

  return value && label ? { value, label } : null;
};

const showIndexLink = (err) => {
  const url = err?.message?.match(/https:\/\/console\.firebase\.google\.com\/[^\s)]+/i)?.[0];
  if (url) {
    toast.info(
      <a href={url} target="_blank" rel="noreferrer">Create required Firestore index</a>,
      { autoClose: 9000, containerId: TOAST_ID }
    );
  }
};

const statusBadge = (status) => {
  const s = norm(status || "scheduled");
  const map = { published: "success", scheduled: "info", ready: "success", draft: "secondary" };
  return <Badge bg={map[s] || "secondary"} className="text-uppercase">{s}</Badge>;
};

const typeBadge = (t) => {
  const s = norm(t);
  const map = { objective: "info", descriptive: "warning" };
  return <Badge bg={map[s] || "light"} text={map[s] ? undefined : "dark"} className="text-uppercase">{t || "—"}</Badge>;
};

const fmtHMS = (seconds) => {
  const d = dayjs.duration(seconds, "seconds");
  const hh = String(Math.floor(d.asHours())).padStart(2, "0");
  const mm = String(d.minutes()).padStart(2, "0");
  const ss = String(d.seconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const getWindowState = (e) => {
  const mode = String(e?.examMode || "live").toLowerCase();
  if (mode === "mock") {
    return { state: "live", start: null, end: null, diffSec: 0, label: "Available anytime" };
  }
  if (!e?.date || !e?.startTime || !e?.endTime) {
    return { state: "unknown", start: null, end: null, diffSec: 0, label: "" };
  }
  const start = dayjs(`${e.date} ${e.startTime}`, "YYYY-MM-DD HH:mm", true);
  const rawEnd = dayjs(`${e.date} ${e.endTime}`, "YYYY-MM-DD HH:mm", true);
  if (!start.isValid() || !rawEnd.isValid()) {
    return { state: "unknown", start: null, end: null, diffSec: 0, label: "" };
  }
  let end = rawEnd;
  if (rawEnd.isBefore(start)) end = start.add(e.durationMins || 0, "minute");
  const now = dayjs();
  if (now.isBefore(start)) {
    const diffSec = start.diff(now, "second");
    return { state: "upcoming", start, end, diffSec, label: `Starts in ${fmtHMS(diffSec)}` };
  }
  if (now.isAfter(end)) return { state: "ended", start, end, diffSec: 0, label: "Ended" };
  const diffSec = end.diff(now, "second");
  return { state: "live", start, end, diffSec, label: `Time left ${fmtHMS(diffSec)}` };
};

const getStudentTimer = (exam, attempt) => {
  const isMock = String(exam?.examMode || "live").toLowerCase() === "mock";
  const dur = Number(exam?.durationMins || 0);
  const started = toJS(attempt?.startedAt);
  const submitted = toJS(attempt?.submittedAt);

  if (!started) return { mode: "not-started", label: "" };
  if (submitted) return { mode: "submitted", label: "Completed" };
  if (isMock || !dur) return { mode: "in-progress", label: "In progress" };

  // personal end
  const personalEnd = dayjs(started).add(dur, "minute");

  // hard-cap by live window end (if any)
  const win = getWindowState(exam); // { state, end, ... }
  const hardEnd = (win?.end && personalEnd.isAfter(win.end)) ? win.end : personalEnd;

  const now = dayjs();
  if (!hardEnd || now.isAfter(hardEnd)) {
    return { mode: "time-up", label: "Time up" };
  }

  const diff = hardEnd.diff(now, "second");
  return { mode: "in-progress", label: `Time left ${fmtHMS(diff)}`, diffSec: diff };
};


const bucketOf = (win) => {
  if (win.state === "live") return "live";
  if (win.state === "upcoming") return win.diffSec <= 24 * 3600 ? "soon" : "later";
  if (win.state === "ended") return "ended";
  return "other";
};

const hasSubmitted = (att) => norm(att?.status) === "submitted";
const inProgress   = (att) => norm(att?.status) === "in-progress";
const blockedByPolicy = (exam, att) => hasSubmitted(att) && !exam?.allowMultipleAttempts;

/** Resume info helper (for objective, live) */
const getResumeInfo = (exam = {}, att = {}) => {
  const allowed = Number.isFinite(Number(exam?.allowedResumes))
    ? Math.max(0, Number(exam.allowedResumes))
    : null; // null => not configured
  const used = Math.max(0, Number(att?.resumeCount || 0));
  const remaining = allowed == null ? null : Math.max(0, allowed - used);
  return { allowed, used, remaining };
};

/* ---------------- Page ---------------- */
export default function StudentExamsPage() {
  const brand = useBrand();
  const { primary } = brand;
  const auth = getAuth();
  const uid = auth.currentUser?.uid || null;
  const nav = useNavigate();

  const [schoolId] = useState(() => localStorage.getItem("schoolId"));
  const [studentId] = useState(() => localStorage.getItem("studentId"));
  const [studentName] = useState(() => localStorage.getItem("studentName") || "");

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [attemptsMap, setAttemptsMap] = useState({});

  const [q, setQ] = useState("");
  const [fSubject, setFSubject] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fMode, setFMode] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [showSchedule, setShowSchedule] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [examForModal, setExamForModal] = useState(null);
  const [ack, setAck] = useState(false);

  /* load profile */
  useEffect(() => {
    if (!schoolId || !studentId) {
      toast.error("Missing student session. Please sign in again.", { containerId: TOAST_ID });
      return;
    }
    setLoadingProfile(true);
    const ref = doc(db, "schools", schoolId, "students", studentId);
    getDoc(ref)
      .then((snap) => {
        if (!snap.exists()) {
          setProfile(null);
          toast.error("Could not find your student profile.", { containerId: TOAST_ID });
        } else {
          setProfile({ id: snap.id, ...snap.data() });
        }
      })
      .catch(() => toast.error("Failed to load profile.", { containerId: TOAST_ID }))
      .finally(() => setLoadingProfile(false));
  }, [schoolId, studentId]);

  const isActive = norm(profile?.status) !== "inactive";

  /* subscribe exams for this class */
  useEffect(() => {
    if (!schoolId || !profile?.className || !isActive) { setExams([]); setLoadingExams(false); return; }
    setLoadingExams(true);
    const col = collection(db, "schools", schoolId, "exams");
    const qy = query(col, where("classIds", "array-contains", profile.className), limit(500));
    const unsub = onSnapshot(qy, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setExams(rows);
      setLoadingExams(false);
    }, (err) => { console.error(err); showIndexLink(err); setLoadingExams(false); });
    return () => unsub();
  }, [schoolId, profile?.className, isActive]);

  /* load attempts for current user */
  useEffect(() => {
    if (!schoolId || !uid || exams.length === 0) { setAttemptsMap({}); return; }
    let cancelled = false;
    (async () => {
      const items = await Promise.all(
        exams.map(async (e) => {
          try {
            const ref = doc(db, "schools", schoolId, "exams", e.id, "attempts", uid);
            const snap = await getDoc(ref);
            return [e.id, snap.exists() ? { id: snap.id, ...snap.data() } : null];
          } catch { return [e.id, null]; }
        })
      );
      if (!cancelled) setAttemptsMap(Object.fromEntries(items));
    })();
    return () => { cancelled = true; };
  }, [schoolId, uid, exams]);

  const subjectOptions = useMemo(() => {
    const s = new Set();
    exams.forEach((e) => { const subj = String(e.subject || e.name || "").trim(); if (subj) s.add(subj); });
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [exams]);

  const modeOptions = useMemo(() => {
    const map = new Map();
    exams.forEach((e) => {
      const meta = getCategoryMeta(e);
      if (meta && !map.has(meta.value)) map.set(meta.value, meta.label);
    });
    const arr = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
    arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return ["all", ...arr.map((m) => m.value)];
  }, [exams]);

  const modeLabelOf = (val) => {
    if (!val || val === "all") return "All modes";
    const hit = exams.find((e) => {
      const meta = getCategoryMeta(e);
      return meta && meta.value === val;
    });
    return hit ? (getCategoryMeta(hit)?.label || prettyTitle(val)) : prettyTitle(val);
  };

  const classTag = useMemo(
    () => [profile?.className, profile?.section].filter(Boolean).join("-") || "—",
    [profile]
  );

  const baseExams = useMemo(() => {
    if (!profile?.className) return [];
    const cls = profile.className;
    const sec = upper(profile?.section || "");
    const matchesSection = (e) => {
      if (Array.isArray(e.targets) && e.targets.length) {
        return e.targets.some((t) => t.classId === cls && (upper(t.sectionKey || "") === sec || (t.sectionKey || "") === ""));
      }
      const map = e.sectionKeysByClass;
      if (map && map[cls]) return map[cls].map((k) => upper(k)).includes(sec) || map[cls].includes("");
      if (Array.isArray(e.classIds) && e.classIds.includes(cls)) return true;
      return false;
    };
    return exams.filter((e) => !!e.isPublished && matchesSection(e));
  }, [exams, profile?.className, profile?.section]);

  const examsWithState = useMemo(
    () => baseExams.map(e => ({ e, ws: getWindowState(e), att: attemptsMap[e.id] || null })),
    [baseExams, attemptsMap]
  );

  const filtered = useMemo(() => {
    const rows = examsWithState
      .filter(({ e, ws }) => {
        if (fSubject !== "all" && String(e.subject || e.name || "").trim() !== fSubject) return false;
        if (fMode !== "all") {
          const meta = getCategoryMeta(e);
          if (!meta || meta.value !== fMode) return false;
        }
        if (fromDate && e.date && dayjs(e.date).isBefore(dayjs(fromDate).startOf("day"))) return false;
        if (toDate && e.date && dayjs(e.date).isAfter(dayjs(toDate).endOf("day"))) return false;
        if (fStatus !== "all" && ws.state !== fStatus) return false;
        if (q.trim()) {
          const cat = getCategoryMeta(e);
          const hay = [
            e.name, e.subject, e.instructions, e.examType,
            cat?.label || "", cat?.value || ""
          ].map((x) => String(x || "").toLowerCase()).join(" ");
          if (!hay.includes(q.trim().toLowerCase())) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const da = String(a.e.date || "");
        const db = String(b.e.date || "");
        if (da !== db) return da.localeCompare(db);
        return String(a.e.startTime || "").localeCompare(String(b.e.startTime || ""));
      });

    const buckets = { live: [], soon: [], later: [], ended: [], other: [] };
    const completed = [];

    rows.forEach((row) => {
      const { ws, att } = row;
      if (norm(att?.status) === "submitted") completed.push(row);
      const k = bucketOf(ws);
      (buckets[k] ||= []).push(row);
    });

    const upcomingFlat = [...buckets.soon, ...buckets.later];
    return { ...buckets, completed, upcomingFlat };
  }, [examsWithState, fSubject, fStatus, fMode, fromDate, toDate, q]);

  const openInstructions = (exam) => { setExamForModal(exam); setAck(false); setShowModal(true); };
  const closeInstructions = () => { setShowModal(false); setExamForModal(null); setAck(false); };

  const navToWrite = (exam) => {
    const t = String(exam?.examType || "").toLowerCase();
    if (t === "descriptive") nav(`/student/exams/${exam.id}/view`);
    else nav(`/student/exams/${exam.id}/write`);
  };

  const ensureAttemptStart = async (exam) => {
    try {
      const ref = doc(db, "schools", schoolId, "exams", exam.id, "attempts", uid);
      const snap = await getDoc(ref);
      const base = {
        uid,
        schoolId,
        examId: exam.id,
        byStudent: { name: studentName || "", id: studentId || "" },
        status: "in-progress",
        startedAt: serverTimestamp(),
        submittedAt: null,
        updatedAt: serverTimestamp(),
      };
      if (!snap.exists()) {
        await setDoc(ref, {
          ...base,
          createdAt: serverTimestamp(),
          durationMins: Number(exam?.durationMins || 0),
          answers: {},
          answeredCount: 0,
          correctCount: 0,
          resumeCount: 0
        }, { merge: true });
      } else {
        const att = snap.data();
        if (norm(att.status) === "submitted" && exam?.allowMultipleAttempts) {
          await setDoc(ref, { ...base, answers: {}, answeredCount: 0, correctCount: 0, resumeCount: 0 }, { merge: true });
        } else if (norm(att.status) !== "in-progress") {
          // switch into in-progress without resetting resumeCount
          await setDoc(ref, { ...base, startedAt: att?.startedAt || serverTimestamp() }, { merge: true });
        }
      }
      setAttemptsMap((m) => ({ ...m, [exam.id]: { ...(m[exam.id] || {}), status: "in-progress" } }));
    } catch (e) {
      console.error(e);
      toast.error("Couldn't start the attempt. Please try again.", { containerId: TOAST_ID });
      throw e;
    }
  };

  /** bump per-student resumeCount exactly on resume (objective, live) */
  const bumpResumeCountIfAllowed = async (exam) => {
    const ref = doc(db, "schools", schoolId, "exams", exam.id, "attempts", uid);
    const snap = await getDoc(ref);
    const att = snap.exists() ? snap.data() : null;
    if (!att || norm(att.status) !== "in-progress") return; // not a resume
    const { allowed, remaining } = getResumeInfo(exam, att);
    if (allowed == null) return; // no limit configured
    if (remaining <= 0) {
      toast.error("Resume limit reached for this exam.", { containerId: TOAST_ID });
      throw new Error("resume-limit");
    }
    await updateDoc(ref, { resumeCount: increment(1), updatedAt: serverTimestamp() });
    setAttemptsMap((m) => ({
      ...m,
      [exam.id]: { ...(m[exam.id] || {}), resumeCount: (m[exam.id]?.resumeCount || 0) + 1 }
    }));
    const left = remaining - 1;
    toast.info(`${left} ${left === 1 ? "resume" : "resumes"} left.`, { containerId: TOAST_ID });
  };

  /* ----- Exam Card: self-updating label ----- */
  function ExamCard({ exam, ws }) {
    const [tick, setTick] = useState(0);
    useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(id); }, []);
    void tick;

    const att = attemptsMap[exam.id] || null;
    const isAnytime = String(exam.examMode || "live").toLowerCase() === "mock";
    const isDesc = norm(exam.examType) === "descriptive";
    const proctorLocked = Boolean(att?.proctor?.locked);

    const curWin = getWindowState(exam);
    const canStartWindow = isAnytime || curWin.state === "live";
    const policyBlocked = blockedByPolicy(exam, att);

    const { allowed: resumesAllowed, remaining: resumesLeft } = getResumeInfo(exam, att);
    const resumeLimitBlocks = !isDesc && canStartWindow && inProgress(att) && (resumesAllowed != null && resumesLeft === 0);

    const canStart  = !isDesc && canStartWindow && !policyBlocked && !hasSubmitted(att);
    const canResume = !isDesc && canStartWindow && inProgress(att) && !resumeLimitBlocks;
    const canRetake = !isDesc && canStartWindow && hasSubmitted(att) && !!exam?.allowMultipleAttempts;

    // Descriptive view-paper rules (with proctor lock in live)
    const canViewPaperBase = isDesc && (isAnytime || curWin.state === "live" || curWin.state === "upcoming");
    const canViewPaper = canViewPaperBase && !(curWin.state === "live" && proctorLocked);

    const studentTimer = !isDesc ? getStudentTimer(exam, att) : { mode: "not-started", label: "" };
    const showStudentTimer = !isDesc && ["in-progress", "time-up", "submitted"].includes(studentTimer.mode);
    const isTimerInProgress = showStudentTimer && studentTimer.mode === "in-progress";
    const submittedAt = toJS(att?.submittedAt);

    const prettyWhen = isAnytime
      ? "Any time"
      : [
          exam.date ? dayjs(exam.date).format("DD MMM YYYY") : "—",
          exam.startTime && exam.endTime ? ` • ${exam.startTime}–${exam.endTime}` : "",
          exam.durationMins ? ` • ${exam.durationMins} mins` : ""
        ].join("");

    const liveBadge = isAnytime ? (
      <Badge bg="success">Available anytime</Badge>
    ) : curWin.state === "live" ? (
      <Badge bg="success">{curWin.label}</Badge>
    ) : curWin.state === "upcoming" ? (
      <Badge bg="info" text="dark">{curWin.label}</Badge>
    ) : curWin.state === "ended" ? (
      <Badge bg="secondary">Ended</Badge>
    ) : null;

    const catMeta = getCategoryMeta(exam);
    const canShowResultsNow = !isDesc && hasSubmitted(att) && (isAnytime || curWin.state === "ended");

    return (
      <Card className="h-100 shadow-sm border-0 exam-card" style={{ borderColor: hexToRgba(primary, 0.22) }}>
        <Card.Body className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="fw-semibold text-truncate" title={exam.name || "Exam"}>{exam.name || "Exam"}</div>
            <div className="d-flex gap-1">{statusBadge(exam.status)}{typeBadge(exam.examType)}</div>
          </div>

          <div className="small text-muted mb-1 d-flex align-items-center gap-1 flex-wrap">
            {exam.subject ? <Badge bg="light" text="dark" className="me-0">{exam.subject}</Badge> : null}
            <Badge bg="light" text="dark" className="me-0">{classTag}</Badge>
            {catMeta ? <Badge bg="light" text="dark" className="me-0">{catMeta.label}</Badge> : null}
            {isDesc && !isAnytime && curWin.state === "live" && proctorLocked && (
              <Badge bg="danger">Proctor Lock</Badge>
            )}
          </div>

          <div className="small text-muted">{prettyWhen}</div>

          <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
            {showStudentTimer ? (
              <Badge bg={studentTimer.mode === "submitted" ? "secondary" : studentTimer.mode === "time-up" ? "danger" : "success"}>{studentTimer.label}</Badge>
            ) : (
              liveBadge
            )}
            {/* Live resume info for objective exams */}
            {!isDesc && !isAnytime && (resumesAllowed != null) && (
              <Badge
                bg={resumesLeft === 0 ? "danger" : "light"}
                text={resumesLeft === 0 ? undefined : "dark"}
                title={resumesLeft === 0 ? "Resume limit reached" : "Maximum resumes allowed for this live exam"}
              >
                {resumesLeft === 0
                  ? "Resume limit reached"
                  : `Resumes left: ${resumesLeft} / ${resumesAllowed}`}
              </Badge>
            )}
          </div>

          {att && (
            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
              {inProgress(att) && !isTimerInProgress && (<Badge bg="warning" text="dark">In progress</Badge>)}
              {hasSubmitted(att) && (
                <>
                  <Badge bg="secondary">Submitted{submittedAt ? ` • ${fmtShort(submittedAt)}` : ""}</Badge>
                  {canShowResultsNow && (
                    <Button size="sm" className="btn-outline-accent" onClick={() => nav(`/student/exams/${exam.id}/results`)}>
                      Show Results
                    </Button>
                  )}
                  {isDesc && att?.upload?.url && (
                    <Button size="sm" variant="outline-secondary" onClick={() => window.open(att.upload.url, "_blank", "noopener")}>
                      View Upload
                    </Button>
                  )}
                  {isDesc && hasSubmitted(att) && (() => {
                    const s = getDescScore(att);
                    const t = getDescTotal(exam, att);
                    if (s == null && t == null) {
                      return <Badge bg="light" text="dark">Pending evaluation</Badge>;
                    }
                    return (
                      <Badge bg="dark">
                        Marks: {s != null ? s : "—"}{t != null ? ` / ${t}` : ""}
                      </Badge>
                    );
                  })()}
                </>
              )}
              {hasSubmitted(att) && exam?.allowMultipleAttempts && (<Badge bg="light" text="dark">Retake allowed</Badge>)}
            </div>
          )}

          <div className="mt-auto pt-2 d-flex justify-content-end gap-2 flex-wrap">
            {isDesc ? (
              <>
                {canViewPaper ? (
                  <Button
                    size="sm"
                    className="btn-outline-accent"
                    onClick={() => nav(`/student/exams/${exam.id}/view`)}
                    title="Open question paper"
                  >
                    View Paper
                  </Button>
                ) : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {!isAnytime && curWin.state === "live" && proctorLocked
                          ? "Proctor lock: questions are hidden."
                          : curWin.state === "upcoming"
                          ? "Not started yet."
                          : curWin.state === "ended"
                          ? "Exam window has closed."
                          : "Not available."}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Button size="sm" className="btn-outline-accent" disabled>
                        View Paper
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
              </>
            ) : (
              (() => {
                const ActionBtn = (
                  <Button
                    size="sm"
                    className={canResume ? "btn-warning" : (canStart || canRetake) ? "btn-accent" : "btn-outline-accent"}
                    disabled={!(canStart || canResume || canRetake)}
                    onClick={() => openInstructions(exam)}
                  >
                    {canResume ? "Resume Test" : canRetake ? "Retake Test" : canStart ? "Start Test" : curWin.state === "upcoming" ? "Starts Soon" : "Closed"}
                  </Button>
                );
                return (canStart || canResume || canRetake) ? ActionBtn : (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {curWin.state === "upcoming"
                          ? "Available only during the scheduled time."
                          : curWin.state === "ended"
                          ? "Exam window has closed."
                          : resumeLimitBlocks
                          ? "Resume limit reached for this exam."
                          : blockedByPolicy(exam, attemptsMap[exam.id])
                          ? "You have already attempted this exam."
                          : "Not available."}
                      </Tooltip>
                    }
                  >
                    <span>{ActionBtn}</span>
                  </OverlayTrigger>
                );
              })()
            )}
          </div>
        </Card.Body>
      </Card>
    );
  }

  /* ----- Section ----- */
  const Section = ({ title, list }) => {
    if (!list?.length) return null;
    return (
      <>
        <div className="d-flex align-items-center gap-2 mt-2 mb-1">
          <div className="fw-semibold">{title}</div>
          <Badge bg="light" text="dark">{list.length}</Badge>
        </div>

        <div className="overflow-auto" style={{ maxHeight: "65vh" }}>
          <Row className="g-3">
            {list.map(({ e, ws }) => (
              <Col key={e.id} xs={12} md={6} lg={4}>
                <ExamCard exam={e} ws={ws} />
              </Col>
            ))}
          </Row>
        </div>
      </>
    );
  };

  return (
    <div className="p-2 p-sm-3">
      <ToastContainer containerId={TOAST_ID} position="bottom-right" newestOnTop closeOnClick={false} pauseOnFocusLoss draggable theme="dark" limit={4} />

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="d-flex flex-column">
          <h5 className="mb-0">Exams</h5>
          <small className="text-muted">
            {studentName || "Student"} • Class { [profile?.className, profile?.section].filter(Boolean).join("-") || "—" }
            {isActive ? "" : " • Inactive"}
          </small>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <Button size="sm" className="btn-outline-accent" onClick={() => setShowSchedule(true)}>View Schedule</Button>
        </div>
      </div>

      {!loadingProfile && !isActive ? (
        <Alert variant="warning" className="mb-3">Your student account is currently <b>inactive</b>. Please contact your school management to activate your account.</Alert>
      ) : null}

      {isActive && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="py-3">
            <Row className="g-2">
              <Col xs={12} md={5} lg={4}>
                <InputGroup>
                  <InputGroup.Text className="bg-light"><i className="bi bi-search" /></InputGroup.Text>
                  <Form.Control placeholder="Search name / subject / instructions / mode" value={q} onChange={(e) => setQ(e.target.value)} />
                </InputGroup>
              </Col>
              <Col xs={6} md={3} lg={3}>
                <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                  {subjectOptions.map((s) => (<option key={s} value={s}>{s === "all" ? "All subjects" : s}</option>))}
                </Form.Select>
              </Col>
              <Col xs={6} md={2} lg={2}>
                <Form.Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="all">All</option>
                  <option value="live">Live / Anytime</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="ended">Ended</option>
                </Form.Select>
              </Col>
              {/* Admin-defined Mode filter */}
              <Col xs={6} md={2} lg={2}>
                <Form.Select value={fMode} onChange={(e) => setFMode(e.target.value)} disabled={modeOptions.length <= 1}>
                  {modeOptions.map((m) => (
                    <option key={m} value={m}>{modeLabelOf(m)}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={1} lg={1}><Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} title="From" /></Col>
              <Col xs={6} md={1} lg={1}><Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} title="To" /></Col>
              <Col xs={12} md={2} lg={1} className="d-grid">
                <Button
                  variant="outline-secondary"
                  onClick={() => { setQ(""); setFSubject("all"); setFStatus("all"); setFMode("all"); setFromDate(""); setToDate(""); }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body>
          {loadingProfile ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
          ) : !isActive ? (
            <div className="text-muted py-4 text-center">Exams are hidden until your account is activated.</div>
          ) : loadingExams ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading exams…</div>
          ) : (baseExams.length === 0) ? (
            <div className="text-muted py-4 text-center">No exams for your class/section.</div>
          ) : (
            <>
              <Section title="Live now"       list={filtered.live} />
              <Section title="Completed"      list={filtered.completed} />
              <Section title="Starting soon"  list={filtered.soon} />
              <Section title="Later"          list={filtered.later} />
              <Section title="Ended"          list={filtered.ended} />
              <Section title="Anytime / Misc" list={filtered.other} />
              {(filtered.live.length + filtered.soon.length + filtered.later.length + filtered.ended.length + filtered.other.length === 0) && (
                <div className="text-muted py-3">No exams match your filters.</div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Upcoming Schedule */}
      <Modal show={showSchedule} onHide={() => setShowSchedule(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Upcoming schedule</Modal.Title></Modal.Header>
        <Modal.Body>
          {filtered.upcomingFlat.length === 0 ? (
            <div className="text-muted">No upcoming exams.</div>
          ) : (
            Object.entries(
              filtered.upcomingFlat.reduce((acc, row) => { const key = row.e.date || "—"; (acc[key] ||= []).push(row); return acc; }, {})
            ).map(([dateKey, list]) => (
              <div key={dateKey} className="mb-3">
                <div className="fw-semibold mb-1">{dayjs(dateKey).isValid() ? dayjs(dateKey).format("DD MMM YYYY") : dateKey}</div>
                <div className="border rounded p-2">
                  {list.map(({ e, ws }) => {
                    const cat = getCategoryMeta(e);
                    const att = attemptsMap[e.id] || null;
                    const isAnytime = String(e.examMode || "live").toLowerCase() === "mock";
                    const proctorLocked = Boolean(att?.proctor?.locked);
                    const canViewPaperBase = norm(e.examType) === "descriptive" && (isAnytime || ws.state === "live" || ws.state === "upcoming");
                    const canViewPaper = canViewPaperBase && !(ws.state === "live" && proctorLocked);

                    // Resume info (objective + live only)
                    const isObjLive = norm(e.examType) === "objective" && !isAnytime && ws.state !== "ended";
                    const { allowed, remaining } = getResumeInfo(e, att);

                    const ViewBtn = (
                      <Button
                        size="sm"
                        className="btn-outline-accent"
                        disabled={!canViewPaper}
                        onClick={() => { setShowSchedule(false); if (canViewPaper) nav(`/student/exams/${e.id}/view`); }}
                      >
                        View Paper
                      </Button>
                    );

                    return (
                      <div key={e.id} className="d-flex align-items-center justify-content-between py-1">
                        <div className="me-2">
                          <div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
                            {e.name}
                            {cat ? <Badge bg="light" text="dark">{cat.label}</Badge> : null}
                            {norm(e.examType) === "descriptive" && !isAnytime && ws.state === "live" && proctorLocked && (
                              <Badge bg="danger">Proctor Lock</Badge>
                            )}
                            {isObjLive && (allowed != null) && (
                              <Badge
                                bg={remaining === 0 ? "danger" : "light"}
                                text={remaining === 0 ? undefined : "dark"}
                                title={remaining === 0 ? "Resume limit reached" : "Maximum resumes allowed for this live exam"}
                              >
                                {remaining === 0
                                  ? "Resume limit reached"
                                  : `Resumes left: ${remaining} / ${allowed}`}
                              </Badge>
                            )}
                          </div>
                          <div className="small text-muted">
                            {e.subject ? `${e.subject} • ` : ""}
                            {e.startTime && e.endTime ? `${e.startTime}–${e.endTime}` : "—"}
                            {e.durationMins ? ` • ${e.durationMins} mins` : ""}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          {norm(e.examType) === "descriptive" ? (
                            canViewPaper ? (
                              ViewBtn
                            ) : (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    {!isAnytime && ws.state === "live" && proctorLocked
                                      ? "Proctor lock: questions are hidden."
                                      : ws.state === "upcoming"
                                      ? "Not started yet."
                                      : ws.state === "ended"
                                      ? "Exam window has closed."
                                      : "Not available."}
                                  </Tooltip>
                                }
                              >
                                <span>{ViewBtn}</span>
                              </OverlayTrigger>
                            )
                          ) : (
                            <Button size="sm" className="btn-outline-accent" onClick={() => { setShowSchedule(false); openInstructions(e); }}>Details</Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSchedule(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Instructions / Start modal (Objective) */}
      <Modal show={showModal} onHide={closeInstructions} centered>
        <Modal.Header closeButton><Modal.Title>Exam Instructions</Modal.Title></Modal.Header>
        <Modal.Body>
          {!examForModal ? (
            <div className="text-center py-3"><Spinner size="sm" /> Loading…</div>
          ) : (
            <>
              <div className="mb-2">
                <div className="fw-semibold">
                  {examForModal.name || "Exam"}{" "}
                  {getCategoryMeta(examForModal) ? (
                    <Badge bg="light" text="dark" className="ms-1">{getCategoryMeta(examForModal).label}</Badge>
                  ) : null}
                </div>
                <div className="text-muted small">
                  {String(examForModal.examMode || "live").toLowerCase() === "mock" ? (
                    <>Any time{examForModal.durationMins ? ` • ${examForModal.durationMins} mins` : ""}</>
                  ) : (
                    <>
                      {examForModal.date ? dayjs(examForModal.date).format("DD MMM YYYY") : "—"}
                      {examForModal.startTime && examForModal.endTime ? (<> • {examForModal.startTime}–{examForModal.endTime}</>) : null}
                      {examForModal.durationMins ? <> • {examForModal.durationMins} mins</> : null}
                    </>
                  )}
                </div>
              </div>

              {examForModal.instructions ? (
                <Alert variant="info"><div className="fw-semibold mb-1">Teacher’s Instructions</div><div className="mb-0">{examForModal.instructions}</div></Alert>
              ) : null}

          {(() => {
  const win = getWindowState(examForModal);
  const att = attemptsMap[examForModal.id] || null;

  if (String(examForModal.examMode || "live").toLowerCase() === "mock") {
    return <Alert variant="success" className="py-2 mb-3">Available anytime</Alert>;
  }

  return (
    <>
      {win.state === "live" && (
        <Alert variant="success" className="py-2 mb-2">Live now • {win.label}</Alert>
      )}
      {att && inProgress(att) && (() => {
        const st = getStudentTimer(examForModal, att);
        return st?.diffSec != null ? (
          <Alert variant="light" className="py-2 mb-3 border">
            Your time left: {st.label.replace("Time left ", "")}
          </Alert>
        ) : null;
      })()}
      {win.state === "upcoming" && (
        <Alert variant="warning" className="py-2 mb-3">Not started • {win.label}</Alert>
      )}
      {win.state === "ended" && (
        <Alert variant="secondary" className="py-2 mb-3">This exam has ended.</Alert>
      )}
    </>
  );
})()}


              {/* Resume policy note (LIVE + objective only) */}
              {(() => {
                const mode = String(examForModal.examMode || "live").toLowerCase();
                const isObjective = norm(examForModal.examType) === "objective";
                if (mode !== "live" || !isObjective) return null;
                const att = attemptsMap[examForModal.id] || {};
                const { allowed, remaining } = getResumeInfo(examForModal, att);
                if (allowed == null) return null;
                const reached = remaining === 0;
                return (
                  <Alert variant={reached ? "danger" : "light"} className="py-2 mb-3 border">
                    <div className="small">
                      <b>Resume policy:</b>{" "}
                      {allowed === 0 ? (
                        <>Resume is <b>disabled</b> for this exam.</>
                      ) : reached ? (
                        <>Resume limit reached (0 / {allowed} left).</>
                      ) : (
                        <>Resumes left: {remaining} / {allowed}.</>
                      )}
                    </div>
                  </Alert>
                );
              })()}

              {(() => {
                const att = attemptsMap[examForModal.id];
                if (!att) return null;
                if (inProgress(att)) return <Alert variant="warning" className="py-2">You have an in-progress attempt.</Alert>;
                if (hasSubmitted(att) && !examForModal.allowMultipleAttempts) return <Alert variant="secondary" className="py-2">You have already submitted this exam. Re-attempt is not allowed.</Alert>;
                if (hasSubmitted(att) && examForModal.allowMultipleAttempts) return <Alert variant="info" className="py-2">Multiple attempts are allowed. You can retake.</Alert>;
                return null;
              })()}

              <ul className="small mb-3">
                <li>Do not reload or close the browser tab during the exam.</li>
                <li>Ensure a stable internet connection and sufficient battery.</li>
                <li>Answers are saved periodically; submit before time ends.</li>
                <li>Switching away from the tab may be monitored by your school.</li>
              </ul>

            {(() => {
  const win = getWindowState(examForModal);
  const att = attemptsMap[examForModal.id] || null;
  const isAnytime = String(examForModal.examMode || "live").toLowerCase() === "mock";
  const canStartWindow = isAnytime || win.state === "live";
  const policyBlocked = blockedByPolicy(examForModal, att);

  // base actions
  let canStart  = canStartWindow && !policyBlocked && !hasSubmitted(att);
  let canResume = canStartWindow && inProgress(att);
  const canRetake = canStartWindow && hasSubmitted(att) && !!examForModal.allowMultipleAttempts;

  // 🔒 resume-limit block (objective + live + in-progress)
  const isObjective = String(examForModal.examType || "").toLowerCase() === "objective";
  const isLive = String(examForModal.examMode || "live").toLowerCase() === "live";
  if (isObjective && isLive && canResume) {
    const { allowed, remaining } = getResumeInfo(examForModal, att);
    if (allowed != null && remaining === 0) {
      canResume = false; // block the resume path
    }
  }

  const actionable = canStart || canResume || canRetake;
  if (!actionable) return null;

  return (
    <div className="form-check">
      <input
        className="form-check-input"
        type="checkbox"
        id="ackRules"
        checked={ack}
        onChange={(e) => setAck(e.target.checked)}
      />
      <label className="form-check-label" htmlFor="ackRules">
        I have read and agree to the instructions.
      </label>
    </div>
  );
})()}

            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeInstructions}>Close</Button>
        {examForModal && norm(examForModal.examType) !== "descriptive" && (() => {
  const win = getWindowState(examForModal);
  const att = attemptsMap[examForModal.id] || null;
  const isAnytime = String(examForModal.examMode || "live").toLowerCase() === "mock";
  const canStartWindow = isAnytime || win.state === "live";
  const policyBlocked = blockedByPolicy(examForModal, att);

  // base actions
  let canStart  = canStartWindow && !policyBlocked && !hasSubmitted(att);
  let canResume = canStartWindow && inProgress(att);
  const canRetake = canStartWindow && hasSubmitted(att) && !!examForModal.allowMultipleAttempts;

  // 🔒 resume-limit block (objective + live + in-progress)
  const isObjective = String(examForModal.examType || "").toLowerCase() === "objective";
  const isLive = String(examForModal.examMode || "live").toLowerCase() === "live";
  let resumeLimitBlocks = false;
  if (isObjective && isLive && canResume) {
    const { allowed, remaining } = getResumeInfo(examForModal, att);
    if (allowed != null && remaining === 0) {
      resumeLimitBlocks = true;
      canResume = false; // block the resume path
    }
  }

  const canShowResults = hasSubmitted(att) && (isAnytime || win.state === "ended");

  // label
  let label = "Start Test";
  if (resumeLimitBlocks) label = "Resume limit reached";
  else if (canResume) label = "Resume Test";
  else if (canRetake) label = "Retake Test";

  // results-only path
  if (canShowResults && !canStart && !canResume && !canRetake) {
    return (
      <Button className="btn-accent" onClick={() => { closeInstructions(); nav(`/student/exams/${examForModal.id}/results`); }}>
        Show Results
      </Button>
    );
  }

  const disabled = !(canStart || canResume || canRetake) || !ack || resumeLimitBlocks;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip>
          {!ack
            ? "Please acknowledge the instructions"
            : resumeLimitBlocks
            ? "You have reached the maximum number of resumes for this live exam."
            : !(canStart || canResume || canRetake)
            ? (win.state === "upcoming"
                ? "Available only during the scheduled time."
                : win.state === "ended"
                ? "Exam window has closed."
                : "Not available.")
            : ""}
        </Tooltip>
      }
    >
      <span>
        <Button
          className="btn-accent"
          disabled={disabled}
          onClick={async () => {
            try {
              if (canResume) {
                // bump and show remaining; throws if limit hit (defensive)
                await bumpResumeCountIfAllowed(examForModal);
              }
              await ensureAttemptStart(examForModal);
              closeInstructions();
              navToWrite(examForModal);
            } catch {
              /* keep modal open if blocked */
            }
          }}
        >
          {label}
        </Button>
      </span>
    </OverlayTrigger>
  );
})()}
        </Modal.Footer>
      </Modal>

      {/* Styles */}
      <style>{`
        .exam-card { border-radius: 18px; background: linear-gradient(180deg, ${hexToRgba(primary, 0.06)} 0%, transparent 60%); transition: transform .18s ease, box-shadow .18s ease; }
        .exam-card:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1.2rem rgba(0,0,0,.12); }
        .btn-accent { 
          --accent: var(--brand-accent, ${brand.accent});
          background-color: var(--accent) !important; 
          border-color: var(--accent) !important; 
          color: var(--on-accent, #fff) !important; 
          box-shadow: 0 .25rem .6rem ${hexToRgba(brand.accent, .35)};
        }
        .btn-accent:hover, .btn-accent:focus { filter: brightness(0.92); color: var(--on-accent, #fff) !important; }
        .btn-accent.disabled, .btn-accent:disabled { opacity: .65 !important; filter: grayscale(.2); box-shadow: none; }
        .btn-outline-accent { 
          --accent: var(--brand-accent, ${brand.accent}); 
          background-color: transparent !important; 
          color: var(--accent) !important; 
          border: 1px solid var(--accent) !important; 
        }
        .btn-outline-accent:hover, .btn-outline-accent:focus { background-color: ${hexToRgba(brand.accent, .12)} !important; color: var(--accent) !important; }
        .btn-warning { color: #000 !important; }
      `}</style>
    </div>
  );
}
